import PropTypes from "prop-types";
import { useCallback, useMemo, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getSettingsMenuItems } from "./settingsMenuItems";
import "./scrollbar-hide.css";

const SettingsTopTabBar = ({ activePage }) => {
    const history = useHistory();
    const { t } = useTranslation("settings");
    const isMountedRef = useRef(true);
    const timeoutRef = useRef(null);

    const activeTabRef = useRef(null);

    const items = useMemo(() => getSettingsMenuItems(t), [t]);

    // Later tabs (Billing, Coupon Code) start off-screen in the scroller; keep the active one visible.
    useEffect(() => {
        activeTabRef.current?.scrollIntoView({ block: "nearest", inline: "center" });
    }, [activePage]);

    useEffect(
        () => () => {
            isMountedRef.current = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        },
        [],
    );

    const handleTabClick = useCallback(
        (value) => {
            if (activePage === value) {
                return;
            }

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    history.push(`/settings?menu=${value}`);
                }
            }, 0);
        },
        [activePage, history],
    );

    return (
        <div
            className="scrollbar-hide -mx-1 flex gap-2 overflow-x-auto px-1 pb-1"
            data-tour="settings-top-tabs"
            role="tablist"
            aria-label={t("settings")}>
            {items.map((item) => {
                const isActive = activePage === item.value;

                return (
                    <button
                        key={item.key}
                        ref={isActive ? activeTabRef : undefined}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        data-tour={item.tourId}
                        onClick={() => handleTabClick(item.value)}
                        className={`shrink-0 rounded-full px-4 py-2 text-[14px] font-medium transition-colors ${
                            isActive
                                ? "bg-[#5D5DF6] text-white shadow-sm"
                                : "bg-white text-[#4B4B5C] ring-1 ring-[#ECECF3] hover:bg-[#F5F5FA]"
                        }`}>
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
};

SettingsTopTabBar.propTypes = {
    activePage: PropTypes.string,
};

export default SettingsTopTabBar;
